import { useRef } from "react";
import { Link } from "react-router-dom";

import { BrandTile, ErrorMessage } from "common";
import useBrandList from "hooks/useBrandList";
import useDismiss from "hooks/useDismiss";
import useHoverIntent from "hooks/useHoverIntent";
import { buildBrandPath } from "utils/productRoutes";

import { NAV_ITEM_CLASS, NAV_MENU_GROUP } from "./navigationItems";

import { Icon } from "@/design-system";

/**
 * The navbar's "Brands" dropdown, ported from the reference's nav (grep `data-brandmenu`
 * in `resources/Dental supply storefront design system/Dens N Dente Tailwind.dc.html`).
 *
 * A single-column 400px panel that opens with the same morph recipe as the category
 * mega-menu, scaled down: `.brand-morph` / `.brand-morph-menu` live in
 * `design-system/components/keyframes.js`, so the panel stays mounted and is driven by
 * `data-open` rather than being conditionally rendered.
 *
 * **Two of the reference's four row columns have no data behind them and are not
 * rendered.** Its rows are `icon · name · tag · count`, off a hand-written `BRANDS`
 * fixture of eight invented brands. Verified against the live API: **0 of those 8 names
 * exist** in our data, so its icon/tag pairings can't be ported the way
 * `ProductsDropdown`'s `CATEGORY_ICON_BY_ID` was (there, 22 of 22 matched).
 *
 * - **tag** ("Exam & surgical gloves") is editorial copy. NetSuite's brand customlist
 *   holds an id and a name only, so writing 313 of these would be inventing product
 *   claims. Dropped.
 * - **count** needs a per-brand `GROUP BY`, which no endpoint exposes; asking for 313
 *   counts one at a time is out of the question behind `pLimit(3)`. Tracked in
 *   `dnd-backend/references/backlog.md` together with the filter rail's facet counts,
 *   which need the same query.
 * - **icon**: the reference's 34x34 tile is kept, filled with the brand's **hosted logo
 *   where the business has one** (36 of 313, `constants/brandLogos.js` — the
 *   `URLS.BRANDS` map, id-keyed) and the brand's first character
 *   otherwise. 313 copies of one generic Material Symbol would carry no information;
 *   the list is `ORDER BY b.name`, so the letter fallback still aids scanning.
 *
 * The panel lists **every** brand — 313 of them — in the reference's own
 * `overflow-y-auto` scroller. That is a deliberate contrast with the filter rail, where
 * inner scrolling was removed: this scroller is the reference's, sized by the panel's
 * `min(496px, 100vh - 180px)`, and the alternative would be curating a shortlist we have
 * no ranking data for.
 *
 * Divergences from the reference beyond the two dropped columns:
 *
 * - Rows are `<Link>`s, not `<button onClick={nav}>`, so middle-click, right-click and
 *   Cmd-click behave. Same call as `ProductsDropdown`.
 * - The trigger is a `<button>` that toggles, where the reference's navigates to the
 *   brands index. `/brands` exists now, but the toggle stays: a navigating trigger is
 *   exactly the shape that made `HoverNavMenu` unusable by tap (it navigates before you
 *   can browse the menu), and the page is one click away via the panel footer anyway.
 * - **It opens on click, not hover**, like `ProductsDropdown`. The trigger toggles it; an
 *   outside click, Escape or choosing a link closes it (`useDismiss`).
 * - The footer is the reference's own **"Browse all brands"** → `/brands`
 *   (`pages/BrandsPage.jsx`). While that route didn't exist it read "Browse all
 *   products"; the stand-in went when the page landed.
 * - **No trailing arrows anywhere in this panel**, on request: neither the reference's
 *   per-row `chevron_right` nor a footer arrow. (The reference's footer arrow is a
 *   hand-drawn animated SVG, `.dnd-lm-arm`, which rule 0 forbids; an `<Icon
 *   name="arrow_forward" />` stood in for it before both went.) `ProductsDropdown` keeps
 *   both of its own, so the two menus deliberately differ — don't "fix" one to match the
 *   other or the reference.
 * - Brands load on **first open**, not on mount, so a page view that never touches the
 *   menu costs nothing. That is `useBrandList`, shared with `MobileDrawer`'s Brands
 *   section — whichever opens first pays for the fetch. `ListProductComponent` fetches the
 *   same endpoint separately for the filter rail, and the backend caches it for 6h
 *   (`TTL.REFERENCE`).
 */
export default function BrandsDropdown() {
  const { brands, loading, error, loadBrands } = useBrandList();
  const { open, onMouseEnter, close } = useHoverIntent(
    undefined,
    undefined,
    NAV_MENU_GROUP
  );
  const rootRef = useRef(null);

  useDismiss(rootRef, open, close);

  const openMenu = () => {
    onMouseEnter();
    loadBrands();
  };

  return (
    <div ref={rootRef} className="relative z-[2]">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => (open ? close() : openMenu())}
        className={`${NAV_ITEM_CLASS} cursor-pointer border-0 bg-transparent font-medium text-ink-2 hover:text-ink`}
      >
        Brands
        <Icon
          name="expand_more"
          size={18}
          weight={400}
          className={`[transition:transform_var(--duration-base)_var(--ease-spring)] ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <div className="brand-morph" data-open={open ? "true" : "false"}>
        <div className="brand-morph-menu">
          <div className="dnd-thinscroll min-h-0 flex-1 overflow-y-auto pr-[4px]">
            {error ? (
              <div className="px-[10px] py-[8px]">
                <ErrorMessage message="Couldn't load brands." type="error" />
              </div>
            ) : loading && !brands.length ? (
              // Six shimmer rows at the real row height, so the panel doesn't jump when
              // the list arrives.
              Array.from({ length: 6 }, (_, index) => (
                <div
                  key={`brand-skel-${index}`}
                  className="flex items-center gap-[10px] px-[10px] py-[8px]"
                >
                  <span className="dnd-skel h-[34px] w-[34px] shrink-0 rounded-md" />
                  <span className="dnd-skel h-[14px] w-[55%] rounded-sm" />
                </div>
              ))
            ) : (
              brands.map((brand) => (
                <Link
                  key={`${brand.brand_id}-${brand.brand_name}`}
                  to={buildBrandPath(brand.brand_name)}
                  onClick={close}
                  className="group/bm flex w-full cursor-pointer items-center gap-[10px] rounded-md bg-transparent px-[10px] py-[8px] text-left font-sans [transition:background_var(--duration-fast),color_var(--duration-fast)] hover:bg-soft focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                >
                  <BrandTile id={brand.brand_id} name={brand.brand_name} />
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink [transition:color_var(--duration-fast)] group-hover/bm:text-blue-700">
                    {brand.brand_name}
                  </span>
                </Link>
              ))
            )}
          </div>

          {/* The reference's own copy and target restored — `onBrandMenuAll` → the
              brands index, which exists now as `/brands`. ("Browse all products" was
              the stand-in while it didn't.) Still no trailing arrow, per request. */}
          <Link
            to="/brands"
            onClick={close}
            className="mt-[6px] flex w-full items-center justify-center gap-[var(--space-2)] border-0 border-t border-line-subtle bg-transparent px-[var(--space-3)] pb-[var(--space-2)] pt-[14px] font-sans text-sm font-semibold text-blue-700 hover:text-blue-800 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
          >
            Browse all brands
          </Link>
        </div>
      </div>
    </div>
  );
}
